import type { BlockDef, CadEntity } from './entity';
import type { Pt } from './geom';
import type { Micron, DisplayUnit } from './units';

/**
 * 도면 문서 (작업지시서 P2-01)
 *
 * cad_version.document_json 에 그대로 직렬화된다.
 * 좌표는 전부 정수 µm 이므로 JSON 왕복에서 정밀도가 손실되지 않는다.
 */

export interface Layer {
  code: string;
  name: string;
  color: string;
  lineType: string;
  /** 1/100 mm (25 = 0.25mm) */
  lineWeight: number;
  visible: boolean;
  locked: boolean;
  printable: boolean;
  sortOrder: number;
}

/**
 * 부착(Attach) 참조 이미지 — 단순 배경용, 편집 대상이 아니다.
 * 일부러 CadEntity 판별 유니온에 넣지 않는다: entityHit/entitySnapPoints/
 * transformEntity 의 switch 가 모든 CadEntity 종류를 컴파일 타임에 강제하므로
 * (entity.ts 상단 주석 참고), 그 유니온 밖에 두면 선택·편집 로직에 실수로
 * 끼어들 여지가 구조적으로 없어진다.
 */
export interface UnderlayRef {
  id: string;
  /** cad-derived 버킷 안의 저장 경로. devPreview 에서는 data: URL. */
  src: string;
  /** 좌상단 기준점 (µm) */
  at: Pt;
  widthUm: Micron;
  heightUm: Micron;
  rotation: number;
  /** 0~1 */
  opacity: number;
  visible: boolean;
}

export interface CadDocument {
  /** 스키마 버전. 마이그레이션 분기에 쓴다. */
  schema: 1;
  /** 좌표 단위 표기. 항상 'um' — 값의 단위를 문서에 못박아 둔다. */
  unit: 'um';
  /** 사용자에게 보여줄 표시 단위 */
  displayUnit: DisplayUnit;
  layers: Layer[];
  blocks: BlockDef[];
  entities: CadEntity[];
  /** 참조용 부착 이미지 (Attach). 없으면 빈 배열 — 예전 문서와 호환. */
  underlays: UnderlayRef[];
}

/**
 * 전사 표준 레이어 11종.
 * supabase/migrations/0003_cad_library.sql 의 cad_layer_template 와 일치해야 한다.
 * (DB 는 관리 화면용, 이 상수는 편집기 부팅용 — 둘을 바꾸면 함께 바꿀 것)
 */
export const STANDARD_LAYERS: Layer[] = [
  l('A-WALL', '벽체·기둥', '#E8E8E8', 50, 10),
  l('A-DOOR', '출입·셔터', '#B7E1FF', 25, 20),
  l('F-FIXTURE', '매대·집기', '#FFD79A', 25, 30),
  l('F-REFRIG', '냉장·냉동', '#9CD8F0', 25, 40),
  l('F-POS', 'POS·계산', '#C7B3FF', 25, 50),
  l('M-ELEC', '전기·설비', '#FFF07A', 18, 60),
  l('M-FIRE', '소방·안전', '#FF9A9A', 25, 70),
  l('O-OPER', '운영 동선', '#A8F0C0', 18, 80),
  l('M-MD', 'MD·진열', '#F5B7E0', 18, 90),
  l('A-DIM', '치수·주석', '#D9D9D9', 18, 100),
  l('A-REF', '참조', '#8A8A8A', 13, 110),
];

function l(
  code: string,
  name: string,
  color: string,
  lineWeight: number,
  sortOrder: number
): Layer {
  return {
    code,
    name,
    color,
    lineType: 'continuous',
    lineWeight,
    visible: true,
    locked: false,
    printable: true,
    sortOrder,
  };
}

export const DEFAULT_LAYER = 'F-FIXTURE';

export function emptyDocument(displayUnit: DisplayUnit = 'mm'): CadDocument {
  return {
    schema: 1,
    unit: 'um',
    displayUnit,
    layers: STANDARD_LAYERS.map((x) => ({ ...x })),
    blocks: [],
    entities: [],
    underlays: [],
  };
}

/**
 * 저장된 JSON 을 문서로 되살린다.
 * 신규 도면(빈 레이어)이나 예전 스키마도 안전하게 받아준다.
 */
export function parseDocument(raw: unknown): CadDocument {
  const base = emptyDocument();
  if (!raw || typeof raw !== 'object') return base;

  const o = raw as Partial<CadDocument>;
  const layers = Array.isArray(o.layers) && o.layers.length > 0 ? o.layers : base.layers;

  return {
    schema: 1,
    unit: 'um',
    displayUnit: o.displayUnit ?? 'mm',
    layers,
    blocks: Array.isArray(o.blocks) ? o.blocks : [],
    entities: Array.isArray(o.entities) ? o.entities : [],
    underlays: Array.isArray(o.underlays) ? o.underlays : [],
  };
}

/** 엔티티 ID 생성. 협업 중 충돌하지 않도록 랜덤을 섞는다. */
export function newEntityId(): string {
  return `e${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}
